import {
    addBulkEditId,
    clearBulkEdit,
    removeBulkEditId,
    setAnchorIndex,
    useBulkEditAnchor,
    useBulkEditPosition,
} from '@/components/grid/bulk-edit-state'
import { GridContext } from '@/components/grid/grid'
import { getIsDeleted, useEditedGif } from '@/context/edit-manager'
import { NexaBlack } from '@/styles/fonts'
import { cn } from '@/util/cn'
import { AttributionOverlay, GifOverlayProps } from '@giphy/react-components'
import { useContext } from 'react'
import { useKeyPress } from 'react-use'

type Props = {
    count?: number
}

const BulkSelectorOverlay = ({ count = 0 }: GifOverlayProps & Props) => {
    return count > 0 ? (
        <div
            className={cn(
                `absolute inset-0 box-border border-[6px] border-solid border-giphyIndigo ${NexaBlack.className}`
            )}
        >
            {count && (
                <div className="absolute top-[-6px] right-[-6px] flex size-9 items-center justify-center bg-giphyYellow text-lg font-extrabold text-black">
                    {count}
                </div>
            )}
        </div>
    ) : null
}

export const BulkEditOverlay = ({ gif }: GifOverlayProps) => {
    const { position } = useBulkEditPosition(gif.id)
    const editedGif = useEditedGif(gif.id)
    const isDeleted = getIsDeleted(editedGif)
    const { gifs } = useContext(GridContext)
    const [isAlt] = useKeyPress('Alt')
    const anchorIndex = useBulkEditAnchor()

    if (isDeleted) {
        return (
            <div
                className={`absolute inset-0 flex items-center justify-center bg-giphyBlack/60 text-2xl text-white ${NexaBlack.className}`}
            >
                DELETED
            </div>
        )
    }
    return isAlt ? (
        <>
            <div className="absolute inset-0 grid place-items-center">
                <div className="text-3xl font-bold uppercase drop-shadow-text-shadow">{gif.rating}</div>
            </div>
            <AttributionOverlay gif={gif} isHovered />
        </>
    ) : position > 0 ? (
        <div
            onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                removeBulkEditId(gif.id)
            }}
            className={cn(
                `absolute inset-0 box-border border-[6px] border-solid border-giphyIndigo ${NexaBlack.className}`
            )}
        >
            {position && (
                <div className="absolute top-[-6px] right-[-6px] flex size-9 items-center justify-center bg-giphyYellow text-lg font-extrabold text-black">
                    {position}
                </div>
            )}
        </div>
    ) : (
        <div
            className="absolute inset-0 cursor-pointer"
            onClick={async (e) => {
                e.preventDefault()
                e.stopPropagation()
                const gridIndex = gifs.findIndex((g) => g.id === gif.id)
                if (gridIndex === -1) return
                if (e.shiftKey && anchorIndex !== null) {
                    const start = Math.min(anchorIndex, gridIndex)
                    const end = Math.max(anchorIndex, gridIndex)
                    clearBulkEdit()
                    let pos = 1
                    for (let i = start; i <= end; i++) {
                        addBulkEditId(gifs[i], pos++)
                    }
                } else if (e.metaKey) {
                    addBulkEditId(gif, gridIndex)
                    setAnchorIndex(gridIndex)
                } else {
                    clearBulkEdit()
                    addBulkEditId(gif, gridIndex)
                    setAnchorIndex(gridIndex)
                }
            }}
        ></div>
    )
}

export default BulkSelectorOverlay
